import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid, Cell } from "recharts";
import { Dot, pct, RAG_ORDER, Skel } from "./shared";

const COLS = [["code", "Project"], ["progress", "Progress"], ["time_elapsed", "Time elapsed"], ["budget_util", "Budget used"],
  ["rag", "RAG"], ["overdue_tasks", "Overdue tasks"], ["missing_updates", "Updates due"]];
const HEX = { red: "#DC2626", amber: "#D97706", green: "#16A34A", grey: "#94A3B8" };

const sorter = (key, dir) => (a, b) => {
  if (key === "risk" || key === "rag") return (RAG_ORDER[a.rag] - RAG_ORDER[b.rag]) * dir || (b.overdue_tasks + b.missing_updates) - (a.overdue_tasks + a.missing_updates);
  const x = a[key], y = b[key];
  if (x == null) return 1;
  if (y == null) return -1;
  return (typeof x === "string" ? x.localeCompare(y) : x - y) * dir;
};

const ChartTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const c = payload[0].payload;
  return <div className="rounded border bg-white p-2 text-xs shadow"><b>{c.code}</b> {c.name}<div>Progress {pct(c.progress)} · Budget {pct(c.budget_util)}</div>{c.gap_flag && <div className="text-amber-700">{c.gap_flag}</div>}</div>;
};

export default function PortfolioTab({ data, onOpen }) {
  const [sort, setSort] = useState({ key: "risk", dir: 1 });
  if (!data) return <Skel rows={6} />;
  const rows = [...data.cards].sort(sorter(sort.key, sort.dir));
  const pts = data.cards.filter(c => c.progress != null && c.budget_util != null);
  const head = (k) => setSort(s => ({ key: k, dir: s.key === k ? -s.dir : 1 }));
  return (
    <div className="grid grid-cols-1 xl:grid-cols-5 gap-4" data-testid="mon-portfolio">
      <Card className="xl:col-span-3"><CardContent className="p-0">
        <div className="px-4 py-3 border-b flex items-center justify-between"><div className="text-sm font-semibold">Portfolio · {rows.length} projects</div>
          <button className="text-xs text-blue-700" onClick={() => setSort({ key: "risk", dir: 1 })} data-testid="mon-sort-risk">Sort by risk</button></div>
        <table className="w-full text-sm hidden md:table" data-testid="mon-portfolio-table">
          <thead className="text-[11px] uppercase text-slate-500 bg-slate-50"><tr>{COLS.map(([k, l]) => (
            <th key={k} className={`px-3 py-2 cursor-pointer select-none ${k === "code" ? "text-left" : "text-right"}`} onClick={() => head(k)} data-testid={`mon-sort-${k}`}>
              {l}{sort.key === k ? (sort.dir > 0 ? " ↑" : " ↓") : ""}</th>))}</tr></thead>
          <tbody>{rows.map(c => (
            <tr key={c.id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => onOpen(c.id)} data-testid={`mon-row-${c.code}`}>
              <td className="px-3 py-2"><div className="flex items-center gap-2"><Dot rag={c.rag} /><span className="font-mono text-xs font-semibold">{c.code}</span><span className="text-xs text-slate-500 truncate max-w-[180px]">{c.name}</span></div></td>
              <td className="px-3 py-2 text-right font-mono">{c.progress == null ? <span className="text-xs text-slate-400">Set targets</span> : pct(c.progress)}</td>
              <td className="px-3 py-2 text-right font-mono">{pct(c.time_elapsed)}</td>
              <td className="px-3 py-2 text-right font-mono">{pct(c.budget_util)}</td>
              <td className="px-3 py-2 text-right"><Dot rag={c.rag} testId={`mon-rag-${c.code}`} /></td>
              <td className="px-3 py-2 text-right font-mono">{c.overdue_tasks}</td>
              <td className="px-3 py-2 text-right font-mono">{c.missing_updates}</td>
            </tr>))}</tbody>
        </table>
        <div className="md:hidden divide-y">{rows.map(c => (
          <button key={c.id} className="w-full text-left px-4 py-3" onClick={() => onOpen(c.id)} data-testid={`mon-mcard-${c.code}`}>
            <div className="flex items-center gap-2"><Dot rag={c.rag} /><b className="font-mono text-xs">{c.code}</b><span className="text-xs text-slate-500 truncate">{c.name}</span></div>
            <div className="text-xs text-slate-600 mt-1">Progress {pct(c.progress)} · Time {pct(c.time_elapsed)} · Budget {pct(c.budget_util)}</div>
          </button>))}</div>
      </CardContent></Card>
      <Card className="xl:col-span-2"><CardContent className="p-4">
        <div className="text-sm font-semibold mb-1">Physical progress vs budget used</div>
        <div className="text-xs text-slate-500 mb-2">One dot per project. Below the line = spending ahead of progress.</div>
        {pts.length === 0 ? <div className="text-xs text-slate-400 py-10 text-center">Projects appear here once targets are set.</div> : (
          <div className="h-72" data-testid="mon-scatter"><ResponsiveContainer>
            <ScatterChart margin={{ top: 10, right: 10, bottom: 10, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" dataKey="budget_util" name="Budget used" unit="%" domain={[0, 100]} fontSize={11} />
              <YAxis type="number" dataKey="progress" name="Progress" unit="%" domain={[0, 100]} fontSize={11} />
              <ReferenceLine segment={[{ x: 0, y: 0 }, { x: 100, y: 100 }]} stroke="#94A3B8" strokeDasharray="4 4" />
              <Tooltip content={<ChartTip />} />
              <Scatter data={pts} onClick={(p) => onOpen(p.id)} cursor="pointer">{pts.map(p => <Cell key={p.id} fill={HEX[p.rag]} />)}</Scatter>
            </ScatterChart></ResponsiveContainer></div>)}
      </CardContent></Card>
    </div>
  );
}
